import { formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import LogoMark from "@/components/LogoMark";
import { downloadImage, printElement } from "@/lib/pdf";
import { Printer, Image as ImageIcon } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("id-ID").format(Number(n || 0));
const DAYS = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const dayName = (iso) => {
  if (!iso) return "________";
  const d = new Date(iso.slice(0, 10));
  return isNaN(d.getTime()) ? "________" : DAYS[d.getDay()];
};

export default function DeliveryNote({ order, open, onClose }) {
  if (!order) return null;
  const o = order;
  const total = o.total_amount || 0;
  const dp = o.down_payment || 0;
  const sisa = Math.max(0, total - dp);

  const isGirl = (o.child_gender || o.gender) === "perempuan";
  const putra = isGirl ? "Putri" : "Putra";
  const sex = (o.lamb_sex || "").toLowerCase() === "betina" ? "BETINA" : "JANTAN";
  const eventDate = o.event_date || o.schedule_date;
  const recvTime = o.delivery_time || o.event_time || "________";
  const phone = o.father_phone || o.mother_phone || "-";
  const penerima = [o.father_name, o.mother_name].filter(Boolean).join(" / ") || "-";

  const rows = [];
  if (o.lamb_type || o.unit_price) rows.push({ label: `Domba : ${(o.lamb_type || "-").toUpperCase()} - ${sex} (${(o.lamb_condition || "dimasak") === "mentah" ? "MENTAH" : "DIMASAK"})`, qty: o.lamb_count || 1, price: o.unit_price || 0, sub: (o.unit_price || 0) * (o.lamb_count || 1) });
  if (o.rice_box_type || o.box_count) rows.push({ label: `Nasi Box : ${(o.rice_box_type || "-").toUpperCase()}`, qty: o.box_count || 0, price: o.box_price || 0, sub: (o.box_price || 0) * (o.box_count || 0) });
  (o.addons || []).forEach((a) => rows.push({ label: `Add On : ${a.name || "-"}`, qty: a.qty || 1, price: a.price || 0, sub: (a.price || 0) * (a.qty || 1) }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto p-0">
        <DialogTitle className="sr-only">Surat Jalan Pesanan Aqiqah {o.order_number}</DialogTitle>
        <div className="flex items-center justify-between border-b border-brand-line px-6 py-4 no-print">
          <h2 className="font-serif text-xl font-bold text-brand-espresso">Berita Acara Pengiriman</h2>
          <div className="flex gap-2">
            <Button onClick={() => downloadImage("delivery-print", `surat-jalan-${o.order_number}.jpg`)} data-testid="jpg-delivery-btn"
              variant="outline" className="gap-2 border-brand-line">
              <ImageIcon className="h-4 w-4" /> JPG
            </Button>
            <Button onClick={() => printElement("delivery-print")} data-testid="print-delivery-btn"
              className="gap-2 bg-brand-gold text-brand-espresso hover:bg-brand-goldhover">
              <Printer className="h-4 w-4" /> Cetak
            </Button>
          </div>
        </div>

        <div id="delivery-print" className="bg-white p-10 text-brand-espresso" style={{ width: "794px", minHeight: "1123px" }}>
          {/* Header */}
          <div className="flex items-center justify-between border-b-2 border-brand-gold pb-4">
            <div className="flex items-center gap-3">
              <LogoMark size={52} textClass="text-3xl" />
              <div className="leading-tight">
                <p className="font-serif text-2xl font-bold">Rizqia Aqiqah</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-brand-muted">Hemat, Praktis, Amanah</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-brand-muted">No. Pesanan</p>
              <p className="font-semibold">{o.order_number}</p>
            </div>
          </div>

          {/* Title */}
          <h1 className="mt-5 text-center font-serif text-2xl font-bold uppercase tracking-wide">Surat Jalan Pesanan Aqiqah</h1>

          {/* Body */}
          <div className="mt-5 text-sm leading-relaxed">
            <p>
              Pada Hari ini, <span className="font-bold">{dayName(eventDate)}</span>, <span className="font-bold">{formatDate(eventDate)}</span> telah dikirimkan{" "}
              <span className="font-semibold">PESANAN AQIQAH</span> atas nama : <span className="font-bold">{(o.child_name || o.aqiqah_name || "-")}</span>,{" "}
              {putra} dari (Ayah): <span className="font-medium">{o.father_name || "-"}</span>, dan (Ibu): <span className="font-medium">{o.mother_name || "-"}</span>, dengan perincian sebagai berikut :
            </p>
          </div>

          {/* Table */}
          <table className="mt-3 w-full border border-brand-line text-sm">
            <thead>
              <tr className="bg-brand-surface/70 text-left">
                <th className="border border-brand-line px-2 py-1.5 text-center w-10">No</th>
                <th className="border border-brand-line px-2 py-1.5">Jenis Pesanan</th>
                <th className="border border-brand-line px-2 py-1.5 text-center w-14">Qty</th>
                <th className="border border-brand-line px-2 py-1.5 text-right w-28">Harga (Rp)</th>
                <th className="border border-brand-line px-2 py-1.5 text-right w-28">Jumlah (Rp)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="border border-brand-line px-2 py-1.5 text-center">{i + 1}</td>
                  <td className="border border-brand-line px-2 py-1.5">{r.label}</td>
                  <td className="border border-brand-line px-2 py-1.5 text-center">{r.qty}</td>
                  <td className="border border-brand-line px-2 py-1.5 text-right">{fmt(r.price)}</td>
                  <td className="border border-brand-line px-2 py-1.5 text-right">{fmt(r.sub)}</td>
                </tr>
              ))}
              {o.shipping_cost > 0 && (
                <tr>
                  <td className="border border-brand-line px-2 py-1.5 text-center">{rows.length + 1}</td>
                  <td className="border border-brand-line px-2 py-1.5" colSpan={3}>Ongkos Kirim</td>
                  <td className="border border-brand-line px-2 py-1.5 text-right">{fmt(o.shipping_cost)}</td>
                </tr>
              )}
              {o.discount > 0 && (
                <tr>
                  <td className="border border-brand-line px-2 py-1.5 text-right" colSpan={4}>Diskon</td>
                  <td className="border border-brand-line px-2 py-1.5 text-right">- {fmt(o.discount)}</td>
                </tr>
              )}
              <tr className="font-semibold">
                <td className="border border-brand-line px-2 py-1.5 text-right" colSpan={4}>TOTAL PESANAN YANG HARUS DIBAYAR</td>
                <td className="border border-brand-line px-2 py-1.5 text-right">{fmt(total)}</td>
              </tr>
              <tr>
                <td className="border border-brand-line px-2 py-1.5 text-right" colSpan={4}>Uang Muka yang sudah dibayarkan</td>
                <td className="border border-brand-line px-2 py-1.5 text-right">{fmt(dp)}</td>
              </tr>
              <tr className="font-bold text-brand-gold">
                <td className="border border-brand-line px-2 py-1.5 text-right" colSpan={4}>SISA PEMBAYARAN</td>
                <td className="border border-brand-line px-2 py-1.5 text-right">{fmt(sisa)}</td>
              </tr>
            </tbody>
          </table>

          {/* Catatan tambahan */}
          <div className="mt-3 rounded-lg border border-brand-line p-3 text-sm">
            <span className="font-semibold">Catatan Tambahan : </span>{o.notes ? o.notes : "-"}
          </div>

          {/* Penerimaan */}
          <div className="mt-3 space-y-1 text-sm leading-relaxed">
            <p>
              Pesanan tersebut telah diterima oleh Pemesan pada Hari : <span className="font-medium">{dayName(eventDate)}</span>, <span className="font-medium">{formatDate(eventDate)}</span> Jam : <span className="font-medium">{recvTime}</span>
            </p>
            <p>
              Alamat Pengiriman Pesanan : <span className="font-medium">{o.delivery_address || "-"}</span> — No. Hp : <span className="font-medium">{phone}</span>
            </p>
          </div>

          {/* Tanggal + tanda tangan */}
          <div className="mt-4 text-right text-sm">Bandung, {formatDate(eventDate || o.created_at)}</div>
          <div className="mt-2 grid grid-cols-2 gap-8 text-center text-sm">
            <div>
              <p>Penerima,</p>
              <div className="mt-16 border-t border-brand-espresso pt-1 font-medium">{penerima}</div>
            </div>
            <div>
              <p>Pengirim,</p>
              <div className="mt-16 border-t border-brand-espresso pt-1 font-medium">( ................................ )</div>
            </div>
          </div>

          <p className="mt-5 text-xs italic text-brand-muted">
            Catatan: Sisa Pembayaran diserahkan kepada Kurir / ditransfer *) &nbsp; <span className="not-italic">*) Coret yang tidak perlu</span>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
